import {
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SearchInput from 'components/SearchInput'
import useAuth from 'hooks/useAuth'
import useLocalStorage from 'hooks/useLocalStorage'
import { usePeriods } from 'hooks/usePeriods'
import { useSearchOrganizationTemplates } from 'hooks/useTemplates'
import type { ChangeEvent, FC, JSX, MouseEvent } from 'react'
import { startTransition, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { TemplateQueryParams } from 'types/comp-template'
import { createIdLabelMap, createMergedIdLabelMap, getClosedPeriodIds } from 'types/periods'
import type { KeyedObject } from 'types/root'
import { getOrganizationLabelMap } from 'types/user-profile'

interface ColumnProps {
  id: string
  label: string
  minWidth: number
  align?: 'right' | 'left' | 'inherit' | 'center' | 'justify' | undefined
  format?: (value: any, row: KeyedObject) => string | boolean | JSX.Element
}

interface TemplatesTableProps {
  withCreateButtons?: boolean
  deleteTemplate: (token: string, id: string) => Promise<{ message: string }>
}

const TemplatesTable: FC<TemplatesTableProps> = ({ withCreateButtons = true, deleteTemplate }) => {
  // Navigation and theme hooks
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { token, profile } = useAuth()

  // Local storage hooks
  const [page, setPage] = useLocalStorage<number>('templates-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('templates-rows-per-page', 10, profile?.user?.id)
  const [search, setSearch] = useLocalStorage<string>('templates-search', '', profile?.user?.id)
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'templates-selected-organizations',
    [],
    profile?.user?.id
  )
  const [sort, setSort] = useLocalStorage<string>('templates-table-sort', 'label', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('templates-table-order', 'asc', profile?.user?.id)
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'templates-selected-periods',
    [],
    profile?.user?.id
  )

  // State hooks
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<{ id: string; name: string } | null>(null)
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')

  // Row actions menu state
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedTemplateForActions, setSelectedTemplateForActions] = useState<any | null>(null)
  const rowActionsOpen = Boolean(rowActionsAnchorEl)

  const handleOpenRowActionsMenu = (event: MouseEvent<HTMLElement>, template: any) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedTemplateForActions(template)
  }

  const handleCloseRowActionsMenu = () => {
    setRowActionsAnchorEl(null)
    setSelectedTemplateForActions(null)
  }

  // Refs
  const tableContainerRef = useRef(null)

  // Query parameters
  const queryParams: TemplateQueryParams = {
    organizationIds: selectedOrganizations,
    searchTerm: search,
    periodIds: selectedPeriods,
    pagination: {
      page: page + 1,
      perPage: rowsPerPage,
    },
    sort: [{ field: sort, order }],
  }

  // Data fetching hooks
  const { templates, pagination, isLoading, refetch } = useSearchOrganizationTemplates(token!, queryParams)
  const { periods, isLoadingPeriods } = usePeriods(token!, rootOrganizationId, rootOrganizationId !== '')

  // Add this after periods is fetched
  const closedPeriodIds = periods?.periods ? getClosedPeriodIds(periods.periods) : []
  const periodLabelMap = periods?.periods ? createMergedIdLabelMap(periods.periods) : {}

  useEffect(() => {
    if (tableContainerRef.current) {
      ;(tableContainerRef.current as HTMLElement).scrollTop = 0
    }
  }, [sort, order, page, rowsPerPage])

  // Handle sorting
  const handleSort = (property: string) => {
    const isAscending = sort === property && order === 'asc'
    setOrder(isAscending ? 'desc' : 'asc')
    setSort(property)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
    setRowsPerPage(+event?.target?.value!)
    setPage(0)
  }

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const organizationMap = profile != null ? getOrganizationLabelMap(profile) : {}

  // table columns
  const columns: ColumnProps[] = [
    {
      id: 'label',
      label: 'Name',
      minWidth: 300,
      format: (value: any, row: any) => {
        if (!row.hasArchivedComponents) return value
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {value}
            <Tooltip title="This template contains archived components">
              <WarningOutlined style={{ color: theme.palette.warning.main, fontSize: '16px', display: 'flex' }} />
            </Tooltip>
          </Box>
        )
      },
    },
    {
      id: 'periodName',
      label: 'Period',
      minWidth: 200,
      format: (value: any, row: any) => {
        return periodLabelMap[row.periodId] || ''
      },
    },
    {
      id: 'organizationName',
      label: 'Team',
      minWidth: 200,
      format: (value: any, row: any) => {
        if (Array.isArray(row.organizationIds) && row.organizationIds.length >= 4) {
          return `(${row.organizationIds.length}) Teams`
        }

        return Array.isArray(row.organizationIds)
          ? row.organizationIds.map((id: string) => organizationMap[id]).join(', ')
          : organizationMap[row.organizationIds]
      },
    },
    { id: 'position', label: 'Position', minWidth: 170 },
    {
      id: 'id',
      label: 'Actions',
      minWidth: 80,
      format: (_templateId: string, row: any) => (
        <Tooltip title="Template Actions">
          <IconButton
            size="medium"
            sx={{ color: theme.palette.primary.main }}
            onClick={(event) => handleOpenRowActionsMenu(event, row)}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  const handleDeleteClick = (templateId: string, templateName: string) => {
    handleCloseRowActionsMenu()
    setTemplateToDelete({ id: templateId, name: templateName })
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (templateToDelete && token) {
      deleteTemplate(token, templateToDelete.id)
        .then(() => {
          setDeleteDialogOpen(false)
          setTemplateToDelete(null)
          refetch() // Trigger a re-fetch of the templates
        })
        .catch((error) => {
          console.error('Failed to delete template:', error)
          // Handle error (e.g., show error message to user)
        })
    }
  }

  useEffect(() => {
    if (profile) {
      const org = profile.roles.find((role) => role.organization.rootOrganizationId != null)
      if (org) {
        setRootOrganizationId(org.organization.rootOrganizationId!)
      }
    }
  }, [profile])

  useEffect(() => {
    if (periods?.periods && selectedPeriods.length === 0) {
      const allPeriodIds = periods.periods.flatMap((period) => {
        const idLabelMap = createIdLabelMap(period)
        return Object.keys(idLabelMap)
      })
      setSelectedPeriods(allPeriodIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods])

  return (
    <>
      <Grid container spacing={4} mb={3}>
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="template-search-input"
            key="template-search-input"
            onSearch={handleSearch}
            initialValue={search}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 3 }}></Grid>
        <Grid size={{ xs: 6 }}>
          <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center">
            {isLoadingPeriods ? (
              <CircularProgress size={20} />
            ) : (
              <MultiPeriodSelector
                periods={periods != null ? periods.periods : []}
                selectedPeriods={selectedPeriods}
                handleChange={setSelectedPeriods}
                label="Filter Periods"
                labelBackgroundColor={theme.palette.background.default}
                width="200px"
              />
            )}
            <MultiOrganizationSelector
              userProfile={profile!}
              selectedOrganizationIds={selectedOrganizations}
              handleChange={setSelectedOrganizations}
              selectAll
              labelBackgroundColor={theme.palette.background.default}
              width="350px"
            />
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedOrganizations([])
                  setSelectedPeriods([])
                  setSearch('')
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
      {isLoading ? (
        <Grid container spacing={12}>
          <Grid
            size={{ xs: 12 }}
            style={{
              display: 'flex', // Enables flexbox
              justifyContent: 'center', // Centers horizontally
              alignItems: 'center', // Centers vertically
              height: '100%', // Takes full height of the container
            }}
          >
            <CircularProgress color="primary" />
            Loading
          </Grid>
        </Grid>
      ) : (
        <Paper>
          {/* table */}
          <TableContainer
            ref={tableContainerRef}
            sx={{
              maxHeight: 'calc(100vh - 400px)',
              minHeight: '300px',
              width: '100%',
            }}
          >
            <Table
              key="templates-table"
              stickyHeader
              size="small"
              aria-label="Templates table"
              sx={{
                minWidth: '1030px',
                width: '100%',
                tableLayout: 'auto',
              }}
            >
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      id={`cell-col-${column.id || ''}`}
                      sx={{
                        minWidth: column.minWidth,
                        position: 'sticky !important',
                      }}
                      key={column.id}
                      align={column.align}
                    >
                      {column.id !== 'label' && column.id !== 'position' ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sort === column.id}
                          direction={sort === column.id ? (order as 'asc' | 'desc') : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length > 0 ? (
                  templates.map((row: KeyedObject) => (
                    <TableRow
                      id={`table-row-${row.id}`}
                      sx={{ py: 3 }}
                      role="checkbox"
                      tabIndex={-1}
                      key={`table-row-${row.id}`}
                    >
                      {columns.map((column) => {
                        const value = row[column.id]
                        return (
                          <TableCell
                            id={`cell-${column.id}`}
                            key={`cell-${column.id}`}
                            align={column.align}
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {column.format ? column.format(value, row) : value}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ px: '20px' }}>
            {withCreateButtons ? (
              <Stack direction="row" spacing={2}>
                <AnimateButton>
                  <Tooltip title="Create a new template">
                    <Button
                      variant="contained"
                      startIcon={<PlusOutlined />}
                      color="primary"
                      onClick={() =>
                        startTransition(() => {
                          navigate('/admin/templates/create')
                        })
                      }
                    >
                      Template
                    </Button>
                  </Tooltip>
                </AnimateButton>
              </Stack>
            ) : (
              <div />
            )}
            {/* table pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Paper>
      )}

      <Menu
        anchorEl={rowActionsAnchorEl}
        open={rowActionsOpen}
        onClose={handleCloseRowActionsMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedTemplateForActions && (
          <>
            {/* ── View ── */}
            <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
              View
            </Typography>
            <MenuItem
              onClick={() => {
                const template = selectedTemplateForActions
                handleCloseRowActionsMenu()
                startTransition(() => {
                  navigate(`/admin/templates/${template.id}/view`)
                })
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <EyeOutlined />
                View Template
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                const template = selectedTemplateForActions
                handleCloseRowActionsMenu()
                startTransition(() => {
                  navigate(`/admin/templates/${template.id}/edit`)
                })
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <EditOutlined />
                Edit Template
              </Box>
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {/* ── Create ── */}
            <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
              Create
            </Typography>
            <Tooltip
              title={
                selectedTemplateForActions.hasArchivedComponents
                  ? 'Cannot duplicate - template contains archived components'
                  : ''
              }
            >
              <span>
                <MenuItem
                  disabled={!!selectedTemplateForActions.hasArchivedComponents}
                  onClick={() => {
                    const template = selectedTemplateForActions
                    handleCloseRowActionsMenu()
                    startTransition(() => {
                      navigate(`/admin/templates/${template.id}/copy`)
                    })
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CopyOutlined />
                    Duplicate Template
                  </Box>
                </MenuItem>
              </span>
            </Tooltip>
            {withCreateButtons && (
              <Tooltip
                title={
                  selectedTemplateForActions.hasArchivedComponents
                    ? 'Cannot create plan - template contains archived components'
                    : closedPeriodIds.includes(selectedTemplateForActions.periodId)
                      ? 'Cannot create plan - period is closed'
                      : ''
                }
              >
                <span>
                  <MenuItem
                    disabled={
                      closedPeriodIds.includes(selectedTemplateForActions.periodId) ||
                      !!selectedTemplateForActions.hasArchivedComponents
                    }
                    onClick={() => {
                      const template = selectedTemplateForActions
                      handleCloseRowActionsMenu()
                      navigate(`/create-comp-plan?templateId=${template.id}`, {
                        state: { from: location.pathname },
                      })
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <PlusCircleOutlined />
                      Create Plan
                    </Box>
                  </MenuItem>
                </span>
              </Tooltip>
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* ── Danger ── */}
            <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
              Danger
            </Typography>
            <MenuItem
              onClick={() => handleDeleteClick(selectedTemplateForActions.id, selectedTemplateForActions.label)}
            >
              <Box display="flex" alignItems="center" gap={1} sx={{ color: 'error.main' }}>
                <DeleteOutlined />
                Delete Template
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-template-dialog"
        aria-describedby="delete-template-description"
      >
        <DialogTitle id="delete-template-dialog">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-template-description">
            Are you sure you want to delete{' '}
            <Typography component="span" color="primary" fontWeight="bold" display="inline">
              {templateToDelete?.name}
            </Typography>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TemplatesTable
